<template>
  <div>
    <!-- Status Card -->
    <div v-if="currentProvisioning" class="mb-4">
      <h5 class="mb-3">{{ $t('google_provisioning.current_status') }}</h5>

      <div class="row mb-3">
        <div class="col-md-6">
          <strong>{{ $t('google_provisioning.status') }}:</strong>
          <span :class="getStatusClass(currentProvisioning.status)" class="ml-2">
            {{ $t('google_provisioning.status_' + currentProvisioning.status) }}
          </span>
        </div>
        <div class="col-md-6">
          <strong>{{ $t('google_provisioning.created_at') }}:</strong>
          <span class="ml-2">{{ formatDate(currentProvisioning.created_at) }}</span>
        </div>
      </div>

      <div v-if="currentProvisioning.google_project_id" class="row mb-3">
        <div class="col-md-6">
          <strong>{{ $t('google_provisioning.project_id') }}:</strong>
          <span class="ml-2">{{ currentProvisioning.google_project_id }}</span>
        </div>
        <div v-if="currentProvisioning.google_project_number" class="col-md-6">
          <strong>{{ $t('google_provisioning.project_number') }}:</strong>
          <span class="ml-2">{{ currentProvisioning.google_project_number }}</span>
        </div>
      </div>

      <!-- Billing warning -->
      <div
        v-if="!currentProvisioning.billing_enabled && currentProvisioning.google_project_id"
        class="alert alert-warning"
      >
        <strong>{{ $t('google_provisioning.billing_disabled_title') }}</strong>
        <p class="mb-1">
          {{ $t('google_provisioning.billing_disabled_text') }}
        </p>
        <div class="mt-2">
          <a
            :href="getBillingUrl(currentProvisioning.google_project_id)"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-sm btn-outline-warning mr-2"
          >
            {{ $t('google_provisioning.open_billing_link') }}
          </a>
          <button
            type="button"
            class="btn btn-sm btn-warning"
            :disabled="checkingBilling"
            @click="checkBilling"
          >
            <span v-if="checkingBilling" class="spinner-border spinner-border-sm mr-1"></span>
            {{ $t('google_provisioning.check_billing_button') }}
          </button>
        </div>
      </div>

      <div v-if="currentProvisioning.error_message" class="alert alert-danger">
        <strong>{{ $t('google_provisioning.error') }}:</strong>
        {{ currentProvisioning.error_message }}
      </div>

      <!-- Logs -->
      <div v-if="currentProvisioning.logs && currentProvisioning.logs.length" class="mt-3">
        <h6>{{ $t('google_provisioning.logs') }}</h6>
        <div class="logs-container" style="max-height: 400px; overflow-y: auto;">
          <div
            v-for="log in currentProvisioning.logs"
            :key="log.id"
            :class="'log-entry log-' + log.level"
          >
            <small>{{ formatDate(log.created_at) }}</small>
            <span>{{ log.message }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Connect Button -->
    <div v-if="!currentProvisioning || (currentProvisioning && currentProvisioning.status === 'failed')" class="text-center">
      <h5 class="mb-3">{{ $t('google_provisioning.connect_title') }}</h5>
      <p class="text-muted mb-4">{{ $t('google_provisioning.connect_description') }}</p>

      <!-- Option to use existing project -->
      <div class="mb-4">
        <div class="form-check mb-3">
          <input
            id="useExistingProject"
            v-model="useExistingProject"
            class="form-check-input"
            type="checkbox"
          />
          <label class="form-check-label" for="useExistingProject">
            {{ $t('google_provisioning.use_existing_project') }}
          </label>
        </div>

        <div v-if="useExistingProject" class="form-group text-left" style="max-width: 500px; margin: 0 auto;">
          <label for="existingProjectId">{{ $t('google_provisioning.existing_project_id') }}</label>
          <input
            id="existingProjectId"
            v-model="existingProjectId"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': existingProjectId && !isValidProjectId(existingProjectId) }"
            maxlength="30"
            :placeholder="$t('google_provisioning.existing_project_id_placeholder')"
            @input="validateProjectId"
          />
          <small v-if="existingProjectId && !isValidProjectId(existingProjectId)" class="form-text text-danger">
            Project ID must be 6-30 characters, start with a letter, contain only lowercase letters, numbers, and hyphens, and end with a letter or number.
          </small>
          <small v-else class="form-text text-muted">
            {{ $t('google_provisioning.existing_project_id_help') }} (6-30 characters, lowercase letters, numbers, and hyphens only)
          </small>
        </div>
      </div>

      <button
        type="button"
        class="btn btn-primary btn-lg"
        :disabled="loading || (useExistingProject && (!existingProjectId || !isValidProjectId(existingProjectId)))"
        @click="connect"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
        <i class="icon-google mr-2"></i>
        {{ $t('google_provisioning.connect_button') }}
      </button>
    </div>

    <!-- Processing Status -->
    <div v-else-if="currentProvisioning && isProcessingStatus(currentProvisioning.status)" class="text-center">
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="sr-only">{{ $t('general.loading') }}</span>
      </div>
      <p class="text-muted">{{ $t('google_provisioning.processing') }}</p>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="refresh"
      >
        {{ $t('general.refresh') }}
      </button>
    </div>

    <!-- Completed Status -->
    <div v-else-if="currentProvisioning && isCompletedStatus(currentProvisioning.status)" class="alert alert-success">
      <h5><i class="icon-checkmark-circle mr-2"></i>{{ $t('google_provisioning.completed') }}</h5>
      <p>{{ $t('google_provisioning.completed_description') }}</p>

      <!-- Credentials Summary -->
      <div v-if="currentProvisioning.api_keys" class="mt-3">
        <h6>{{ $t('google_provisioning.credentials') }}</h6>
        <ul class="list-unstyled">
          <li v-if="currentProvisioning.api_keys.browser">
            <strong>{{ $t('google_provisioning.browser_api_key') }}:</strong>
            <code class="ml-2">{{ currentProvisioning.api_keys.browser.key_string }}</code>
          </li>
          <li v-if="currentProvisioning.api_keys.server">
            <strong>{{ $t('google_provisioning.server_api_key') }}:</strong>
            <code class="ml-2">{{ currentProvisioning.api_keys.server.key_string }}</code>
          </li>
          <li v-if="currentProvisioning.service_account_credentials">
            <strong>{{ $t('google_provisioning.service_account') }}:</strong>
            <span class="ml-2">{{ $t('google_provisioning.configured') }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ProvisioningContent',
  props: {
    isPublic: {
      type: Boolean,
      default: false,
    },
    currentProvisioning: {
      type: Object,
      default: null,
    },
    provisioningList: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    checkingBilling: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      useExistingProject: false,
      existingProjectId: '',
    }
  },
  mounted() {

  },
  methods: {
    isProcessingStatus(status) {
      return status && ['authenticating', 'creating_project', 'enabling_apis', 'creating_credentials'].includes(status)
    },
    isCompletedStatus(status) {
      return status === 'completed'
    },
    isFailedStatus(status) {
      return status === 'failed'
    },
    isValidProjectId(projectId) {
      if (!projectId) return false
      // Google Cloud project_id rules:
      // - 6 to 30 characters long
      // - must start with a letter
      // - can contain lowercase letters, numbers, and hyphens
      // - must end with a letter or number (not hyphen)
      if (projectId.length < 6 || projectId.length > 30) {
        return false
      }
      if (!/^[a-z]/.test(projectId)) {
        return false
      }
      if (!/^[a-z0-9-]+$/.test(projectId)) {
        return false
      }
      if (!/[a-z0-9]$/.test(projectId)) {
        return false
      }
      return true
    },
    validateProjectId() {
      // Trim and convert to lowercase on input
      if (this.existingProjectId) {
        this.existingProjectId = this.existingProjectId.trim().toLowerCase()
      }
    },
    connect() {
      // Validate project ID if using existing project
      if (this.useExistingProject && this.existingProjectId && !this.isValidProjectId(this.existingProjectId)) {
        this.$parent.$toast.error('Please enter a valid project ID (6-30 characters, lowercase letters, numbers, and hyphens only)')
        return
      }

      // Check if we're in slave mode - if so, open master auth instead
      const parent = this.$parent
      if (parent.isSlaveMode) {
        parent.openMasterAuth()
      } else {
        // Pass useExistingProject and existingProjectId to parent
        parent.connectGoogle(this.useExistingProject, this.existingProjectId)
      }
    },
    refresh() {
      this.$parent.refreshStatus()
    },
    checkBilling() {
      this.$parent.checkBillingStatus()
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm:ss')
    },
    getStatusClass(status) {
      const classes = {
        pending: 'badge badge-warning',
        authenticating: 'badge badge-info',
        creating_project: 'badge badge-info',
        enabling_apis: 'badge badge-info',
        creating_credentials: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
      }
      return classes[status] || 'badge badge-secondary'
    },
    getBillingUrl(projectId) {
      if (!projectId) {
        return 'https://console.cloud.google.com/billing'
      }
      return `https://console.cloud.google.com/billing/linkedaccount?project=${projectId}`
    },
  },
}
</script>

<style scoped>
.logs-container {
  border: 1px solid #dadada;
  padding: 10px 15px;
  border-radius: 4px;
  background-color: #f8f9fa;
}

.log-entry {
  padding: 8px;
  margin-bottom: 5px;
  border-radius: 4px;
  border-left: 3px solid #ddd;
}

.log-entry.log-info {
  background-color: #e3f2fd;
  border-left-color: #2196f3;
}

.log-entry.log-success {
  background-color: #e8f5e9;
  border-left-color: #4caf50;
}

.log-entry.log-warning {
  background-color: #fff3e0;
  border-left-color: #ff9800;
}

.log-entry.log-error {
  background-color: #ffebee;
  border-left-color: #f44336;
}

.log-entry small {
  color: #666;
  margin-right: 10px;
  font-weight: 500;
}

code {
  background-color: #f4f4f4;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}
</style>
